// ====================================================================================
// COMPONENT: CampaignPsychographicBriefing (Aesthetic: Clean Dark Glass - No Gradients)
// Self-contained: synthesizes ARTEMIS psychographic summary. All icons and logic included.
// ====================================================================================
import {
  Brain as BrainIcon,
  Shield,
  Heart,
  AlertCircle,
  CheckCircle,
  TrendingUp as TrendIcon,
  MessageSquare,
  Zap,
  Sparkles,
} from "lucide-react";

const CampaignPsychographicBriefing = ({ swingData }) => {
  if (!swingData || !swingData.summary) {
    return null;
  }

  const {
    dominant_messaging_frames,
    dominant_persuasion_pathways,
    dominant_activation_vectors,
  } = swingData.summary;

  // --- 1. Analyze Moral Axis ---
  const topFrameEntry = Object.entries(dominant_messaging_frames || {}).sort((a, b) => b[1] - a[1])[0] || ['Balanced', 0];
  const [topFrameName] = topFrameEntry;

  let moralAxis = {
    icon: BrainIcon,
    color: 'text-slate-400',
    title: topFrameName,
    implication: "Voters are morally balanced. Use a mix of compassion-based and community-based arguments.",
  };

  if (topFrameName.includes('Community')) {
    moralAxis = {
      icon: Shield,
      color: 'text-blue-400',
      title: "Community & Security Axis",
      implication: "Frame messages around 'protecting our local way of life' and 'common-sense rules.' Appeals to tradition and group cohesion are most effective.",
    };
  } else if (topFrameName.includes('Compassion')) {
    moralAxis = {
      icon: Heart,
      color: 'text-emerald-400',
      title: "Compassion & Fairness Axis",
      implication: "Frame messages around 'helping those in need' and 'ensuring a fair chance for everyone.' Empathy-driven arguments are most effective.",
    };
  }

  // --- 2. Analyze Persuasion Pathways ---
  const pathways = Object.entries(dominant_persuasion_pathways || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // --- 3. Analyze Activation Barriers ---
  const topActivationEntry = Object.entries(dominant_activation_vectors || {}).sort((a, b) => b[1] - a[1])[0] || ['Standard voter.', 0];
  const [topActivationName] = topActivationEntry;

  let activationBarrier = {
    icon: AlertCircle,
    color: 'text-rose-400',
    title: "High Barrier: Cynicism",
    recommendation: "Standard GOTV will fail. Focus on concrete, local impacts to overcome belief that the system doesn't respond.",
  };

  if (topActivationName.includes('Low Difficulty')) {
    activationBarrier = {
      icon: CheckCircle,
      color: 'text-emerald-400',
      title: "Low Barrier: Engaged",
      recommendation: "Voters are engaged and confident. A simple, polite reminder of the polling date and its local impact is sufficient.",
    };
  } else if (topActivationName.includes('Medium Difficulty') || topActivationName.includes('Standard voter')) {
    activationBarrier = {
      icon: TrendIcon,
      color: 'text-amber-400',
      title: "Medium Barrier: Standard",
      recommendation: "Standard GOTV protocols (e.g., 3-touch reminder) apply. Requires consistent, standard effort.",
    };
  }

  // --- Helper: Micro Header ---
  const HeaderLabel = ({ text, icon: Icon, colorClass }) => (
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className={colorClass} />
      <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{text}</h4>
    </div>
  );

  return (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-4 sm:p-6 relative overflow-hidden">

      {/* Component Header */}
      <div className="flex items-center gap-3 mb-8 border-b border-slate-800/50 pb-5">
        <div className="p-2 bg-slate-800 rounded-lg border border-slate-700 shadow-md flex-shrink-0">
          <BrainIcon className="text-white" size={20} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white uppercase tracking-tight">Psychographic Briefing</h3>
          <p className="text-xs text-slate-400 font-medium mt-0.5">Cognitive & Behavioral Analysis</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Card 1: Moral Axis */}
        <div className="bg-slate-950/40 p-6 rounded-xl border border-slate-800 hover:border-slate-600 transition-all duration-300 h-full group flex flex-col">
          <HeaderLabel text="Dominant Moral Axis" icon={moralAxis.icon} colorClass={moralAxis.color} />

          <p className="text-lg font-bold text-white mb-6 leading-tight">{moralAxis.title}</p>

          <div className="mt-auto bg-slate-900/40 rounded-lg p-4 border border-slate-800/50">
            <h5 className="text-[9px] text-slate-500 uppercase font-bold mb-2 flex items-center gap-1.5">
              <Sparkles size={12} className="text-accent-400" /> Strategic Implication
            </h5>
            {/* UPDATED: text-xs -> text-sm for better readability */}
            <p className="text-sm text-slate-300 leading-relaxed font-medium">{moralAxis.implication}</p>
          </div>
        </div>

        {/* Card 2: Persuasion Pathways */}
        <div className="bg-slate-950/40 p-6 rounded-xl border border-slate-800 hover:border-slate-600 transition-all duration-300 h-full flex flex-col">
          <HeaderLabel text="Top Persuasion Pathways" icon={MessageSquare} colorClass="text-indigo-400" />

          <div className="space-y-3 flex-grow">
            {pathways.length > 0 ? pathways.map(([name, count], idx) => (
              <div key={name} className="flex justify-between items-center p-3 rounded-lg border border-slate-800/50 bg-slate-900/30 hover:bg-slate-800/50 transition-colors group">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[10px] text-slate-600 font-mono font-bold flex-shrink-0">0{idx + 1}</span>
                  <span className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors truncate">{name}</span>
                </div>
                <span className="text-[10px] font-mono font-bold text-indigo-300 bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20 shadow-sm flex-shrink-0 ml-2">{count} Wards</span>
              </div>
            )) : (
              <p className="text-xs text-slate-500 italic">No pathway data available.</p>
            )}
          </div>
        </div>

        {/* Card 3: Activation Barrier */}
        <div className="bg-slate-950/40 p-6 rounded-xl border border-slate-800 hover:border-slate-600 transition-all duration-300 h-full flex flex-col">
          <HeaderLabel text="Primary Activation Barrier" icon={activationBarrier.icon} colorClass={activationBarrier.color} />

          <p className="text-lg font-bold text-white mb-6 leading-tight">{activationBarrier.title}</p>

          <div className="mt-auto bg-slate-900/40 rounded-lg p-4 border border-slate-800/50">
            <h5 className="text-[9px] text-slate-500 uppercase font-bold mb-2 flex items-center gap-1.5">
              <Zap size={12} className="text-amber-400" /> GOTV Recommendation
            </h5>
            {/* UPDATED: text-xs -> text-sm for better readability */}
            <p className="text-sm text-slate-300 leading-relaxed font-medium">{activationBarrier.recommendation}</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CampaignPsychographicBriefing;
